import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, FileText, AlertCircle } from 'lucide-react';
import { contentService } from '@/services/api';
import { getMailHref } from '@/lib/contactLinks';

const PrivacyPage = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'ar').toLowerCase().startsWith('ar') ? 'ar' : 'en';
  const isRTL = lang === 'ar';

  const [privacyContent, setPrivacyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const lastUpdated = lang === 'ar' ? '1 فبراير 2026' : 'February 1, 2026';

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // First try to fetch from custom_pages endpoint
        const response = await contentService.getCustomPages();
        if (response.data?.data && Array.isArray(response.data.data)) {
          // Find the Privacy Policy page
          const privacyPage = response.data.data.find((page) => {
            const title = (page.title || '').toLowerCase();
            const slug = (page.slug || '').toLowerCase();
            const type = (page.page_type || '').toLowerCase();
            return slug.includes('privacy') || type === 'user_privacy_policy' || title.includes('privacy');
          });
          
          if (privacyPage?.content) {
            setPrivacyContent(privacyPage.content);
            return;
          }
        }
        
        // If custom_pages didn't work, try direct privacy endpoint
        const privacyResponse = await contentService.getPrivacyPolicy();
        const htmlContent = typeof privacyResponse.data === 'string' 
          ? privacyResponse.data 
          : privacyResponse.data?.content || privacyResponse.data?.data || '';
        
        // Extract just the body content if full HTML is returned
        let bodyContent = htmlContent;
        if (htmlContent.includes('<body>')) {
          const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
          if (bodyMatch) {
            bodyContent = bodyMatch[1];
          }
        }
        
        if (bodyContent) {
          setPrivacyContent(bodyContent);
        } else {
          throw new Error('No content found');
        }
      } catch (err) {
        console.error('Error fetching privacy policy:', err);
        // Use static fallback content instead of showing error
        setPrivacyContent(getStaticPrivacy(lang));
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, [lang]);

  // Static fallback content
  const getStaticPrivacy = (language) => {
    if (language === 'ar') {
      return `
        <h2>سياسة الخصوصية</h2>
        <p>نلتزم في دكترها بحماية خصوصيتك والحفاظ على سرية بياناتك الشخصية.</p>
        <h3>المعلومات التي نجمعها</h3>
        <p>نجمع معلومات مثل الاسم والبريد الإلكتروني ورقم الهاتف وبعض المعلومات الطبية اللازمة لتقديم الخدمة.</p>
        <h3>كيف نستخدم معلوماتك</h3>
        <p>نستخدم هذه المعلومات لتقديم خدماتنا الطبية وتحسينها وتسهيل حجز المواعيد والتواصل معك.</p>
        <h3>حماية البيانات</h3>
        <p>نتخذ إجراءات أمنية مناسبة لحماية بياناتك، بما في ذلك استخدام تقنيات التشفير SSL/TLS.</p>
        <h3>حقوقك</h3>
        <p>يحق لك الوصول إلى بياناتك الشخصية وطلب تصحيحها أو حذفها وفقًا للأنظمة المعمول بها.</p>
      `;
    }
    return `
      <h2>Privacy Policy</h2>
      <p>At Dactrha, we are committed to protecting your privacy and the security of your personal data.</p>
      <h3>Information We Collect</h3>
      <p>We collect personal information such as name, email, phone number, and medical information.</p>
      <h3>How We Use Your Information</h3>
      <p>We use information to provide and improve our medical services and facilitate appointment booking.</p>
      <h3>Data Protection</h3>
      <p>We take strict security measures to protect your data using SSL/TLS encryption.</p>
      <h3>Your Rights</h3>
      <p>You have the right to access, rectify, or delete your data.</p>
    `;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" dir={isRTL ? 'rtl' : 'ltr'} data-testid="privacy-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white mb-4">
            <Shield className="h-4 w-4 mr-1" />
            {lang === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {lang === 'ar' ? 'خصوصيتك تهمنا' : 'Your Privacy Matters'}
          </h1>
          <p className="text-teal-100">
            {lang === 'ar' ? `آخر تحديث: ${lastUpdated}` : `Last updated: ${lastUpdated}`}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {loading ? (
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-8 w-1/2 mt-6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="dark:bg-slate-800 border-0 shadow-sm bg-red-50 dark:bg-red-900/20">
            <CardContent className="p-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </CardContent>
          </Card>
        ) : null}

        {/* Privacy Content */}
        {!loading && (
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-6 md:p-8">
              <div 
                className="prose prose-slate dark:prose-invert max-w-none
                  prose-headings:text-slate-900 dark:prose-headings:text-white
                  prose-h1:text-2xl prose-h1:font-bold prose-h1:mb-4 prose-h1:text-center
                  prose-h2:text-xl prose-h2:font-semibold prose-h2:mt-8 prose-h2:mb-4 prose-h2:text-teal-700 dark:prose-h2:text-teal-400
                  prose-h3:text-lg prose-h3:font-medium prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-4
                  prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
                  prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
                  prose-li:text-slate-600 dark:prose-li:text-slate-300 prose-li:mb-2
                  prose-strong:text-slate-900 dark:prose-strong:text-white prose-strong:font-semibold"
                dangerouslySetInnerHTML={{ __html: privacyContent }}
              />
            </CardContent>
          </Card>
        )}

        {/* Contact */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm mt-8 bg-teal-50 dark:bg-teal-900/20">
          <CardContent className="p-6 text-center">
            <FileText className="h-12 w-12 text-teal-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {lang === 'ar' ? 'هل لديك أسئلة؟' : 'Have Questions?'}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {lang === 'ar' 
                ? 'إذا كانت لديك أي أسئلة حول سياسة الخصوصية، يرجى التواصل معنا.'
                : 'If you have any questions about our privacy policy, please contact us'}
            </p>
            <a
              href={getMailHref('privacy@dactrha.com')}
              className="text-teal-600 font-medium transition-colors hover:text-teal-700 hover:underline dark:text-teal-400"
            >
              <span dir="ltr">privacy@dactrha.com</span>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;

